import type { WovarProduct } from "@commercekit/api";
import type { Money } from "@commercekit/shared";
import { optimizeImageUrl } from "~/lib/imageUtils";
import {
  PRODUCT_SWITCHER_GROUPS,
  type ProductSwitcherAttributeConfig,
  type ProductSwitcherGroupConfig,
  type ProductSwitcherTranslations,
} from "./config";

type SwitcherPrices = Pick<
  WovarProduct,
  "priceInclTax" | "priceExclTax" | "salePriceInclTax" | "salePriceExclTax"
>;

export interface ProductSwitcherOption {
  value: string;
  slug: string;
  active: boolean;
  image: { url: string; alt: string } | null;
  prices: SwitcherPrices | null;
}

export interface ProductSwitcherAttribute {
  key: string;
  label: string;
  selectedLabel: string;
  display: ProductSwitcherAttributeConfig["display"];
  options: ProductSwitcherOption[];
}

export interface ProductSwitcher {
  id: string;
  priceSuffix: string;
  attributes: ProductSwitcherAttribute[];
}

export const findProductSwitcherGroup = (
  sku: string | null | undefined,
  locale: string,
  groups: ProductSwitcherGroupConfig[] = PRODUCT_SWITCHER_GROUPS
) =>
  groups.find(
    (group) =>
      group.locales.includes(locale.toLowerCase()) &&
      group.products.some((product) => product.sku === sku)
  ) ?? null;

// Falls back to Dutch when a language has no text yet.
const translate = (translations: ProductSwitcherTranslations | undefined, language: string) =>
  translations?.[language] ?? translations?.nl ?? "";

export const buildProductSwitcher = (
  group: ProductSwitcherGroupConfig,
  currentSku: string,
  products: WovarProduct[],
  locale: string
): ProductSwitcher | null => {
  const language = locale.toLowerCase().split("-")[0];
  const valueLabel = (value: string) =>
    group.valueLabels?.[value] ? translate(group.valueLabels[value], language) : value;
  const bySku = new Map(products.map((product) => [product.sku, product]));
  // Group members the API didn't return (offline, not in this channel) are left out.
  const members = group.products
    .filter((member) => bySku.has(member.sku))
    .map((member) => ({ ...member, product: bySku.get(member.sku)! }));
  const current = members.find((member) => member.sku === currentSku);
  if (!current || members.length < 2) return null;

  const matchCount = (member: (typeof members)[number], skipKey: string) =>
    group.attributes.filter(
      ({ key }) => key !== skipKey && member.values[key] === current.values[key]
    ).length;

  const attributes = group.attributes.map((attribute) => {
    const others = group.attributes.length - 1;
    const values = [...new Set(members.map((member) => member.values[attribute.key]))];

    const options = values.flatMap((value): ProductSwitcherOption[] => {
      // Only offer values that exist in combination with the current product's other values.
      const target = members.find(
        (member) =>
          member.values[attribute.key] === value && matchCount(member, attribute.key) === others
      );
      if (!target) return [];

      const { product } = target;
      const imageUrl = optimizeImageUrl(
        product.defaultImage?.urlOriginal || product.defaultImage?.urlThumbnail,
        { width: 200 }
      );
      return [
        {
          value: valueLabel(value),
          slug: product.slug,
          active: target.sku === current.sku,
          image:
            attribute.display === "image" && imageUrl
              ? { url: imageUrl, alt: product.defaultImage?.altText || product.name }
              : null,
          prices:
            attribute.display === "price"
              ? {
                  priceInclTax: product.priceInclTax,
                  priceExclTax: product.priceExclTax,
                  salePriceInclTax: product.salePriceInclTax,
                  salePriceExclTax: product.salePriceExclTax,
                }
              : null,
        },
      ];
    });

    const selectedValue = current.values[attribute.key];
    return {
      key: attribute.key,
      label: translate(attribute.label, language),
      selectedLabel: (translate(attribute.selectedTemplate, language) || "{value}").replace(
        "{value}",
        valueLabel(selectedValue)
      ),
      display: attribute.display,
      options,
    };
  });

  return { id: group.id, priceSuffix: translate(group.priceSuffix, language), attributes };
};

// Same price the PDP order box shows for that product.
export const getSwitcherDisplayPrice = (
  prices: SwitcherPrices,
  notB2B: boolean
): Money | null | undefined => {
  const onSale = (prices.salePriceInclTax?.value ?? 0) > 0;
  if (notB2B) return onSale ? prices.salePriceInclTax : prices.priceInclTax;
  return onSale ? prices.salePriceExclTax : prices.priceExclTax;
};
